import { OpenAICompatibleChatLanguageModel } from '@ai-toolkit/openai-compatible';
import {
  LanguageModelV1,
  NoSuchModelError,
  ProviderV1,
} from '@ai-toolkit/provider';
import {
  FetchFunction,
  loadApiKey,
  withoutTrailingSlash,
} from '@ai-toolkit/provider-utils';
import {
  CerebrasChatModelId,
  CerebrasChatSettings,
} from './cerebras-chat-settings';
import { z } from 'zod';
import { ProviderErrorStructure } from '@ai-toolkit/openai-compatible';

// Add error schema and structure
const cerebrasErrorSchema = z.object({
  message: z.string(),
  type: z.string(),
  param: z.string(),
  code: z.string(),
});

export type CerebrasErrorData = z.infer<typeof cerebrasErrorSchema>;

const cerebrasErrorStructure: ProviderErrorStructure<CerebrasErrorData> = {
  errorSchema: cerebrasErrorSchema,
  errorToMessage: data => data.message,
};

export interface CerebrasProviderSettings {
  /**
Cerebras API key.
*/
  apiKey?: string;
  /**
Base URL for the API calls.
*/
  baseURL?: string;
  /**
Custom headers to include in the requests.
*/
  headers?: Record<string, string>;
  /**
Custom fetch implementation. You can use it as a middleware to intercept requests,
or to provide a custom fetch implementation for e.g. testing.
*/
  fetch?: FetchFunction;
}

export interface CerebrasProvider extends ProviderV1 {
  /**
Creates a Cerebras model for text generation.
*/
  (
    modelId: CerebrasChatModelId,
    settings?: CerebrasChatSettings,
  ): LanguageModelV1;

  /**
Creates a Cerebras model for text generation.
*/
  languageModel(
    modelId: CerebrasChatModelId,
    settings?: CerebrasChatSettings,
  ): LanguageModelV1;

  /**
Creates a Cerebras chat model for text generation.
*/
  chat(
    modelId: CerebrasChatModelId,
    settings?: CerebrasChatSettings,
  ): LanguageModelV1;
}

export function createCerebras(
  options: CerebrasProviderSettings = {},
): CerebrasProvider {
  const baseURL = withoutTrailingSlash(
    options.baseURL ?? 'https://api.cerebras.ai/v1',
  );
  const getHeaders = () => ({
    Authorization: `Bearer ${loadApiKey({
      apiKey: options.apiKey,
      environmentVariableName: 'CEREBRAS_API_KEY',
      description: 'Cerebras API key',
    })}`,
    ...options.headers,
  });

  const createLanguageModel = (
    modelId: CerebrasChatModelId,
    settings: CerebrasChatSettings = {},
  ) => {
    return new OpenAICompatibleChatLanguageModel(modelId, settings, {
      provider: `cerebras.chat`,
      url: ({ path }) => `${baseURL}${path}`,
      headers: getHeaders,
      fetch: options.fetch,
      defaultObjectGenerationMode: 'tool',
      errorStructure: cerebrasErrorStructure,
    });
  };

  const provider = (
    modelId: CerebrasChatModelId,
    settings?: CerebrasChatSettings,
  ) => createLanguageModel(modelId, settings);

  provider.languageModel = createLanguageModel;
  provider.chat = createLanguageModel;
  provider.textEmbeddingModel = (modelId: string) => {
    throw new NoSuchModelError({ modelId, modelType: 'textEmbeddingModel' });
  };

  return provider;
}

export const cerebras = createCerebras();
